import { encodeVoiceIdentity } from "@vidimo/shared";
import { useCallback, useMemo } from "react";
import type { useHistory } from "react-router-dom";
import { APP_PUSH_STATE } from "@/lib/history-entry";
import { routeTemplates } from "@/lib/routes";
import { parseCaller, type RawInvite } from "./caller";
import type { ActiveCall, PendingInvite } from "./voice-context";

/**
 * Real Twilio CallSids start with "CA"; the dev-menu simulators mint theirs
 * with this prefix instead. A simulated call has no counterpart in the native
 * plugin and no Twilio leg, so every action that would reach out (accept,
 * reject, end, mute, transfer) short-circuits to local state on these.
 */
const SIMULATED_CALL_SID_PREFIX = "DEV";

export function isSimulatedCall(callSid: string): boolean {
  return callSid.startsWith(SIMULATED_CALL_SID_PREFIX);
}

function mintSimulatedCallSid(): string {
  return `${SIMULATED_CALL_SID_PREFIX}${Math.random().toString(36).slice(2, 14).toUpperCase()}`;
}

/** A teammate the dev menu places a fake call from. */
export interface SimulatedStaffCaller {
  memberId: string;
  name: string;
  propertyId: string;
}

/** The dev-menu-only slice of the voice context. */
export interface CallSimulators {
  /**
   * Dev-only: inject a fake `activeCall` (status "connected") and navigate
   * to /active-call. Auto-clears after 30s so the fake doesn't leak. Like
   * `simulateInboundInvite`, this is UI-only — no audio, no native plugin
   * involvement.
   */
  simulateActiveCall: () => void;
  /**
   * Dev-only: inject a fake `pendingInvite` and navigate to /incoming-call
   * so the UI can be inspected without placing a real Twilio call. Does
   * NOT exercise CallKit / push — for that, use the
   * /dev/simulate-inbound-call endpoint instead.
   */
  simulateInboundInvite: () => void;
  /**
   * Dev-only: inject a fake staff-to-staff `pendingInvite` from `caller` and
   * navigate to /incoming-call so IncomingStaffCallView (kind="staff"
   * branch) can be inspected without placing a real Twilio call.
   */
  simulateStaffInboundInvite: (caller: SimulatedStaffCaller) => void;
}

const SIMULATED_CALL_DURATION_MS = 30_000;

/**
 * Builds the simulators the voice context exposes to the dev menu. They write
 * straight to the provider's own call state, so the UI can't tell a simulated
 * call from a real one.
 */
export function useCallSimulators({
  activePropertyId,
  dropCall,
  historyRef,
  recordCall,
  recordInvite,
}: {
  activePropertyId: string | null;
  dropCall: (callSid: string, options: { handBack: boolean }) => boolean;
  historyRef: { current: ReturnType<typeof useHistory> };
  recordCall: (next: ActiveCall | null) => void;
  recordInvite: (next: PendingInvite | null) => void;
}): CallSimulators {
  const pushSimulatedInvite = useCallback(
    (invite: RawInvite) => {
      recordInvite({
        caller: { ...parseCaller(invite), propertyId: activePropertyId },
        callSid: mintSimulatedCallSid(),
      });
      const h = historyRef.current;
      if (h.location.pathname !== routeTemplates.app.incomingCall) {
        h.push(routeTemplates.app.incomingCall, APP_PUSH_STATE);
      }
    },
    [activePropertyId, historyRef, recordInvite]
  );

  const simulateInboundInvite = useCallback(() => {
    // A random number outside the seeded range, so the caller lookup
    // resolves to nothing: the "unknown caller" UI is what this demos.
    pushSimulatedInvite({
      from: `+1555${Math.floor(1_000_000 + Math.random() * 9_000_000)}`,
    });
  }, [pushSimulatedInvite]);

  const simulateStaffInboundInvite = useCallback(
    ({ memberId, name, propertyId }: SimulatedStaffCaller) => {
      // What a real staff dial delivers on Android: the caller's name in
      // `from` and in its param, the identity in its own.
      pushSimulatedInvite({
        customParams: {
          CapacitorTwilioCallerIdentity: encodeVoiceIdentity(
            memberId,
            propertyId
          ),
          CapacitorTwilioCallerName: name,
        },
        from: name,
      });
    },
    [pushSimulatedInvite]
  );

  const simulateActiveCall = useCallback(() => {
    const fakeSid = mintSimulatedCallSid();
    const startedAt = Date.now();
    // Default to Thomas Dubois — handcrafted seed guest with avatar +
    // confirmed reservation on unit 203 at emerald-valley. Exercises the
    // rich-data path (name, avatar, reservation pill) so the developer
    // can compare it against the unknown-caller UI from the inbound
    // simulator above. The `dev` field carries the synthetic context so
    // the UI renders the rich path even when seed data hasn't synced
    // down yet — the live `useCallerLookup` would otherwise miss and
    // we'd show the bare displayName.
    recordCall({
      caller: {
        ...parseCaller({ from: "+33 6 39 98 89 01" }),
        propertyId: activePropertyId,
      },
      callSid: fakeSid,
      dev: {
        reservationStatus: "confirmed",
        unitCode: "203",
      },
      direction: "incoming",
      displayName: "Thomas Dubois",
      muted: false,
      onHold: false,
      startedAt,
      status: "connected",
    });
    recordInvite(null);
    const h = historyRef.current;
    if (h.location.pathname !== routeTemplates.app.activeCall) {
      h.push(routeTemplates.app.activeCall, APP_PUSH_STATE);
    }
    // Auto-clear so the dev fake doesn't leak across sessions. The
    // user can also tap the End button (which falls through the
    // DEV-SID short-circuit in endCall).
    window.setTimeout(() => {
      dropCall(fakeSid, { handBack: true });
    }, SIMULATED_CALL_DURATION_MS);
  }, [activePropertyId, dropCall, historyRef, recordCall, recordInvite]);

  return useMemo(
    () => ({
      simulateActiveCall,
      simulateInboundInvite,
      simulateStaffInboundInvite,
    }),
    [simulateActiveCall, simulateInboundInvite, simulateStaffInboundInvite]
  );
}
